import { DomainError } from "./model.ts";

export const CHIP_DENOMINATIONS = Object.freeze([25_000, 5_000, 1_000, 500, 100, 25] as const);

export interface ChipCount {
  readonly denomination: (typeof CHIP_DENOMINATIONS)[number];
  readonly count: number;
}

export interface ChipDecomposition {
  readonly chips: readonly ChipCount[];
  readonly representedTotal: number;
  readonly remainder: number;
}

export function roundWholePoints(value: number | null): number | null {
  if (value === null) return null;
  if (!Number.isFinite(value)) throw new DomainError("INVALID_POINTS", "points must be finite or null");
  return Math.round(value);
}

export function formatWholePoints(value: number | null, empty = "—"): string {
  const rounded = roundWholePoints(value);
  return rounded === null ? empty : new Intl.NumberFormat("en-AU", { maximumFractionDigits: 0 }).format(rounded);
}

/** Greedy decomposition uses precise banked points. The remainder is retained
 * for display copy; a rounded label must never fabricate an additional chip. */
export function decomposeChips(value: number): ChipDecomposition {
  if (!Number.isFinite(value) || value < 0) throw new DomainError("INVALID_POINTS", "chip totals must be finite and non-negative");
  let remainder = value;
  const chips: ChipCount[] = [];
  for (const denomination of CHIP_DENOMINATIONS) {
    const count = Math.floor(remainder / denomination);
    if (count > 0) chips.push({ denomination, count });
    remainder -= count * denomination;
  }
  const representedTotal = chips.reduce((sum, chip) => sum + chip.denomination * chip.count, 0);
  return { chips, representedTotal, remainder: value - representedTotal };
}

